import React, { useCallback, useEffect } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Image, Layout, Space, Typography, Upload } from 'antd';
import { Link, NavLink, Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DeleteOutlined, ProfileTwoTone } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import { useCollectionById, useCollectionImage } from 'src/features/collections/collections.hooks';
import { Collection } from 'src/features/collections/collections.types';
import { deleteCollection, fetchCollection, updateCollection } from 'src/features/collections/collections.state';
import localStyles from 'src/components/pages/Company/CompanyPage/CompanyPage.module.css';
import RouteProtected from 'src/components/blocks/RouteProtected/RouteProtected';
import CollectionContentCardsTab from 'src/components/pages/Content/CollectionPage/CollectionContentCardsTab';
import InputEditableField from 'src/components/fields/InputEditableField';
import SwitchSelectField from 'src/components/fields/SwitchSelectField';
import { FormattedMessage } from 'react-intl';
import { formatDatetime } from 'src/helpers/formatters';
import Loader from 'src/components/atoms/Loader';
import { UploadFile } from 'antd/es/upload/interface';
import SelectEditableField from 'src/components/fields/SelectEditableField';
import CollectionContentLocalesTab from 'src/components/pages/Content/CollectionPage/CollectionContentLocalesTab';

const { Content } = Layout;

const CollectionPage: React.FC = () => {
  const { collectionId } = useParams() as { collectionId: string };
  const dispatch: AppDispatch = useDispatch();
  const collection = useCollectionById(collectionId) as Collection;
  const history = useHistory();
  const { uploadContentImage } = useCollectionImage({
    collectionId,
  });

  const handleDeleteButtonClick = useCallback(async () => {
    const response = await dispatch(deleteCollection({ collectionId }));
    if (deleteCollection.fulfilled.match(response)) {
      history.push(`/content/collections`);
    }
  }, [dispatch, collectionId, history]);
  const handleCollectionUpdate = useCallback(
    (data: Partial<Collection>) => {
      dispatch(updateCollection({ collectionId, data }));
    },
    [collectionId, dispatch]
  );

  const handleUpload = useCallback(
    (file: UploadFile) => {
      uploadContentImage(file as unknown as File);
    },
    [uploadContentImage]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/content/collections'>
                <Space>
                  <ProfileTwoTone />
                  Коллекции
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/content/collections/${collectionId}`}>{collection.name}</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Space direction={'vertical'} size={16}>
            <InputEditableField level={2} value={collection.name} onBlur={e => handleCollectionUpdate({ name: e })} />
            <Space size='small' wrap>
              <NavLink to={`cards`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <ProfileTwoTone />
                  Видео
                </Button>
              </NavLink>
              <NavLink to={`locales`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <ProfileTwoTone />
                  Переводы
                </Button>
              </NavLink>
            </Space>
          </Space>
          <Content>
            <Route
              render={({ match: { path } }) => (
                <Switch>
                  <RouteProtected exact path={`${path}/cards`} component={CollectionContentCardsTab} />
                  <RouteProtected exact path={`${path}/locales`} component={CollectionContentLocalesTab} />
                  <Redirect from={path} to={`${path}/cards`} exact />
                </Switch>
              )}
            />
          </Content>
        </div>
        <div className={styles.sidebar}>
          <Space direction={'vertical'}>
            {collection.image && <Image src={collection.image} width={160} />}
            <Upload
              beforeUpload={() => false}
              accept={'image/*'}
              showUploadList={false}
              onChange={e => handleUpload(e.file)}
            >
              <Button type={'primary'}>{collection.image ? 'Обновить постер' : 'Загрузить постер'}</Button>
            </Upload>
          </Space>
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>Заблокирован</Typography.Text>
            <SwitchSelectField value={collection.disabled} onSelect={e => handleCollectionUpdate({ disabled: e })} />
            <Typography.Text className={styles.title}>Категория контента</Typography.Text>
            <SelectEditableField
              value={collection.category || '-'}
              options={[
                { label: 'VR Cinema', value: 'vr_cinema' },
                { label: 'Мир VR', value: 'vr_world' },
                { label: '18+', value: 'adult' },
                { label: 'Kids', value: 'kids' },
              ]}
              onSelect={e => handleCollectionUpdate({ category: e })}
            />
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.createdAt' />: {formatDatetime(collection.createdAt)}
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.updatedAt' />: {formatDatetime(collection.updatedAt)}
            </Typography.Text>
            <ConfirmButton type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
              <DeleteOutlined /> Удалить коллекцию
            </ConfirmButton>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CollectionPageBlock: React.FC = () => {
  const { collectionId } = useParams() as { collectionId: string };
  const dispatch = useDispatch();
  const collection = useCollectionById(collectionId);

  useEffect(() => {
    dispatch(fetchCollection({ collectionId }));
  }, [dispatch, collectionId]);

  if (!collection) {
    return <Loader />;
  }

  return <CollectionPage />;
};

export default CollectionPageBlock;
